import { useQuery } from "@tanstack/react-query";

import {
  api,
  type CallsReport,
  type LaneWorkload,
  type ReportScope,
} from "../../../lib/api";
import {
  withCardFilters,
  type DashboardLayout,
} from "../../../lib/dashboard-layout";
import { queryKeys } from "../../../lib/queryKeys";
import { DASHBOARD_STALE_MS } from "../../../lib/dashboard-refresh";
import { rangeIsReady, toRangeValue } from "../RangeFilter";
import { CardChart, ReportCard } from "../card/ReportCard";
import { TicketCount } from "../card/TicketCount";
import type { ChartType } from "../card/chart-types";
import { formatSeconds } from "./CallsReport";

/**
 * Who is carrying what, across every channel the desk works in.
 *
 * The question it exists for: *is this person behind on tickets because they
 * are on the phone all morning?* A ticket count alone cannot answer that, and
 * without an answer the obvious reading of a low number is the wrong one.
 *
 * **Calls sit beside tickets in the same bars.** Taken and made, per person,
 * from the phone system's own history, so a short ticket bar next to a long
 * call bar reads as what it is. The slower figures -- median call, talk time,
 * missed -- are in a list underneath rather than in the chart, because seconds
 * and counts on one axis would make one of them unreadable.
 *
 * **Without the phone system it says so rather than hiding it.** The card was
 * shipped before the integration with the call columns drawn as an explicit
 * gap, and a server without Yay credentials still draws that gap: an empty
 * column labelled "not connected" tells a manager what is missing from the
 * reading, where leaving it out would make the ticket counts look like the
 * whole picture.
 */

const CHARTS: ChartType[] = ["bar", "stacked-bar", "table"];

export function WorkloadHeatmap({
  layout,
  scope: pageScope,
  isManager,
}: {
  layout: DashboardLayout;
  /** Only a manager is offered the Agent filter; the server enforces it. */
  isManager: boolean;
  scope: ReportScope;
}) {
  const stored = layout.card("workload");
  /* The card's own Pipeline and Agent filters, folded into the page's
     scope so they reach both the query and its key. */
  const scope = withCardFilters(pageScope, stored);
  const range = toRangeValue(stored.range, { preset: "this-month" });
  const ready = rangeIsReady(range);

  /*
   * Tickets per agent over *this card's* range.
   *
   * It used to read the weekly report's month slot, which is a fixed window:
   * the date picker in this card's header moved and every bar stood still. The
   * range now reaches the query, which also means it reaches the query key, so
   * changing it refetches with no further wiring.
   */
  const report = useQuery({
    queryKey: queryKeys.dashboardReport("agent-workload", range, scope),
    queryFn: () => api.agentWorkload(range, scope),
    enabled: ready,
    staleTime: DASHBOARD_STALE_MS,
  });

  const telephony = useQuery({
    queryKey: ["integrations", "telephony"],
    queryFn: api.telephonyStatus,
    staleTime: 30 * 60_000,
  });

  /*
   * Open Lane tasks per person, and the one figure on this card the range does
   * not reach.
   *
   * Half of that is deliberate and half is a limit worth writing down. An
   * unfinished task is unfinished *now* whatever month it was raised in, so
   * putting the open count behind a date filter would let "this week" show
   * somebody as up to date while forty things sat on their list -- the range
   * would make that number wrong, not narrower.
   *
   * Completed tasks are a different matter and could honestly be ranged, but
   * Lane's workload endpoint answers with a count of open against a count of
   * total and no completion dates, so there is nothing here to range by. It
   * needs Lane to say *when*, which is a change in Lane. The section says so
   * on screen rather than letting a date picker above it imply otherwise.
   */
  const workload = useQuery({
    queryKey: queryKeys.dashboardReport("lane-workload", null, scope),
    queryFn: () => api.laneWorkload(scope),
    /* A minute, matching the overdue card. This reads a dozen boards out of
       another product, and a task closed thirty seconds ago is not more
       interesting than one closed a minute ago. */
    staleTime: DASHBOARD_STALE_MS,
  });

  const phoneConnected = telephony.data?.enabled === true;

  /*
   * The same report the Calls card reads, for this card's range and filters.
   * Sharing the query key means that when both cards are on the same period
   * the figures are fetched once and cannot disagree.
   */
  const calls = useQuery({
    queryKey: queryKeys.dashboardReport("calls", range, scope),
    queryFn: () => api.callsReport(range, scope),
    enabled: ready && phoneConnected,
    staleTime: DASHBOARD_STALE_MS,
  });

  const byAgent = report.data?.byAgent ?? [];
  const callRows = calls.data?.enabled ? calls.data.byAgent : [];
  const rows = mergeRows(byAgent, callRows);

  const chartSeries = phoneConnected ? WITH_CALLS : TICKETS_ONLY;
  /* One series is coloured per person, as the card always was; several are
     coloured per series, or every bar for one person would be one colour. */
  const series = phoneConnected
    ? chartSeries.map((entry) => ({ ...entry }))
    : rows.map((row) => ({ key: row.label, label: row.label }));

  return (
    <ReportCard
      id="workload"
      title="Agent workload"
      subtitle="Tickets alongside calls and tasks, so a quiet ticket count can be read properly"
      layout={layout}
      offerAgents={isManager}
      charts={CHARTS}
      series={series}
      footnote={
        telephony.data?.enabled ? null : (
          <p>
            Call figures need the Yay integration, which is not connected on
            this server. Until it is, this card reads tickets only — so a light
            ticket count here is not yet evidence of a light workload.
          </p>
        )
      }
    >
      {({ chart, colorFor }) => {
        if (!ready) {
          return (
            <p className="py-6 text-center text-xs text-faint">
              Pick both dates.
            </p>
          );
        }

        if (rows.length === 0) {
          return (
            <p className="py-6 text-center text-xs text-faint">
              {report.isLoading || calls.isLoading
                ? "Loading…"
                : phoneConnected
                  ? "Nobody has raised anything or been on the phone yet."
                  : "Nobody has raised anything yet."}
            </p>
          );
        }

        return (
          <div className="space-y-3">
            <CardChart
              chart={chart}
              rows={rows.map((row) => ({ label: row.label, values: row.values }))}
              series={[...chartSeries]}
              renderValue={(row, key, value) =>
                key === "tickets" ? (
                  <TicketCount
                    count={value ?? 0}
                    dimension="raised-agent"
                    value={String(
                      rows.find((entry) => entry.label === row.label)?.ticketUserId ??
                        "",
                    )}
                    label={`${row.label} — raised in this period`}
                    range={range}
                    scope={scope}
                  />
                ) : (
                  (value ?? 0).toLocaleString()
                )
              }
              colorBy={phoneConnected ? "series" : "row"}
              colorFor={colorFor}
              format={(value) => (value === null ? "—" : value.toLocaleString())}
            />

            {phoneConnected ? (
              <PhoneTime data={calls.data} loading={calls.isLoading} />
            ) : null}

            <LaneTasks data={workload.data} loading={workload.isLoading} />

            {/*
              The channels that are not wired, named individually rather than
              as one apologetic sentence. A manager looking at this should be
              able to see exactly which measurements are missing from the
              picture they are forming.
            */}
            {telephony.data?.enabled ? null : (
              <div className="rounded-md border border-dashed border-line px-3 py-2">
                <p className="mb-1.5 text-[11px] uppercase tracking-wide text-faint">
                  Waiting on Yay
                </p>
                <ul className="grid gap-x-4 gap-y-0.5 text-[11px] text-faint sm:grid-cols-2 lg:grid-cols-3">
                  {[
                    "Inbound calls taken",
                    "Outbound calls made",
                    "Median call duration",
                    "Missed calls",
                    "Call abandonment rate",
                  ].map((measure) => (
                    <li key={measure} className="flex items-baseline gap-1.5">
                      <span
                        aria-hidden
                        className="size-1 shrink-0 rounded-full bg-line"
                      />
                      {measure}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );
      }}
    </ReportCard>
  );
}

const TICKETS_ONLY = [{ key: "tickets", label: "Tickets" }] as const;

const WITH_CALLS = [
  { key: "tickets", label: "Tickets" },
  { key: "taken", label: "Calls taken" },
  { key: "made", label: "Calls made" },
] as const;

interface WorkloadRow {
  label: string;
  /** For the ticket count's drill-down. Null for the unassigned row. */
  ticketUserId: number | null;
  values: Record<string, number | null>;
}

/**
 * Tickets and calls as one row per person.
 *
 * Joined on the Pulse user id, never on the name: two people can share one,
 * and an agent renamed this morning would otherwise appear twice. Somebody on
 * the phone who has raised no tickets still gets a row -- that is the person
 * this card exists to explain -- and a phone user nobody has been linked to
 * appears under the phone system's name with its extension, so the admin who
 * reads it knows which one to pick in Settings.
 */
function mergeRows(
  tickets: { key: string; count: number; userId: number | null }[],
  calls: Extract<CallsReport, { enabled: true }>["byAgent"],
): WorkloadRow[] {
  const rows: WorkloadRow[] = tickets.map((row) => ({
    label: row.key,
    ticketUserId: row.userId,
    values: { tickets: row.count },
  }));

  if (calls.length === 0) return rows;

  for (const row of rows) {
    row.values.taken = 0;
    row.values.made = 0;
  }

  for (const call of calls) {
    const existing =
      call.userId === null ? undefined : rows.find((row) => row.ticketUserId === call.userId);

    if (existing) {
      existing.values.taken = call.taken;
      existing.values.made = call.made;
      continue;
    }

    rows.push({
      label: call.linked
        ? call.name
        : `${call.name}${call.extension ? ` (ext. ${call.extension})` : ""}`,
      ticketUserId: call.userId,
      values: { tickets: 0, taken: call.taken, made: call.made },
    });
  }

  return rows;
}

/**
 * Time on the phone, per person: the figures that are not counts.
 *
 * Median call, talk time and missed calls, under the bars rather than in them.
 * "Missed" here is calls rung straight to that person; a missed call to a
 * shared line is nobody's, and is on the Calls card's desk view instead.
 */
function PhoneTime({
  data,
  loading,
}: {
  data: CallsReport | undefined;
  loading: boolean;
}) {
  if (loading || !data?.enabled || data.byAgent.length === 0) return null;

  return (
    <div className="rounded-md border border-line">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 border-b border-line px-3 py-1.5">
        <span className="text-[11px] uppercase tracking-wide text-faint">
          On the phone
        </span>
        <span className="text-[11px] text-muted">
          median call and talk time with customers, for the chosen period
        </span>
      </div>

      <ul>
        {data.byAgent.map((person) => (
          <li
            key={person.userId ?? person.platformUserId ?? `ext:${person.extension ?? person.name}`}
            className="flex items-baseline gap-3 px-3 py-1 odd:bg-surface even:bg-app"
          >
            <span className="min-w-0 flex-1 truncate text-xs text-content">
              {person.name}
              {!person.linked ? (
                <span
                  className="ml-1.5 text-[10px] uppercase text-faint"
                  title="Link this phone user to somebody in Settings → Users"
                >
                  not linked
                </span>
              ) : null}
            </span>
            <span className="shrink-0 text-[11px] tabular-nums text-muted" title="Median answered call">
              {formatSeconds(person.medianSeconds)}
            </span>
            <span className="w-16 shrink-0 text-right text-[11px] tabular-nums text-muted" title="Talk time">
              {formatTalkShort(person.talkSeconds)}
            </span>
            <span
              className="w-16 shrink-0 text-right text-[11px] tabular-nums text-faint"
              title="Missed calls rung straight to them"
            >
              {person.missed > 0 ? `${person.missed} missed` : ""}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function formatTalkShort(seconds: number): string {
  const minutes = Math.round(seconds / 60);
  const hours = Math.floor(minutes / 60);
  return hours === 0 ? `${minutes}m` : `${hours}h ${String(minutes % 60).padStart(2, "0")}m`;
}

/**
 * Open Lane tasks, per person.
 *
 * The half of "workload" the ticket count cannot see. An agent with a light
 * ticket month may be carrying thirty tasks, and until this was here the card
 * showed the first number and none of the second -- which is exactly the
 * misreading its own subtitle warns against.
 *
 * Only people Lane has assigned something to, ever, appear. Padding the list
 * out with every agent on the desk would put a row of zeroes against people
 * who simply do not work in Lane, and a zero that means "not applicable"
 * reads identically to one that means "nothing left to do".
 *
 * Which is why finishing your list keeps you on it, marked up to date rather
 * than shown as a nought. Those two states are the whole point of the section
 * and they must not look the same.
 */
function LaneTasks({
  data,
  loading,
}: {
  data: LaneWorkload | undefined;
  loading: boolean;
}) {
  /* Nothing while loading, nothing when Lane is off, nothing when the boards
     hold no assigned work at all. */
  if (loading || !data?.enabled || data.people.length === 0) return null;

  const outstanding = data.people.reduce((sum, person) => sum + person.open, 0);

  return (
    <div className="rounded-md border border-line">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 border-b border-line px-3 py-1.5">
        <span className="text-[11px] uppercase tracking-wide text-faint">
          Open Lane tasks
        </span>
        <span className="text-[11px] text-muted">
          {outstanding} outstanding, excluding anything done or archived
        </span>

        {/* Because the card above it has a date filter and this does not, and
            a reader has no way to tell which figures moved when they changed
            it. Lane reports a count and not a date -- see the query's note. */}
        <span className="text-[11px] text-faint">· as it stands now, not for the chosen period</span>

        {/* Said rather than swallowed: a short list that looks complete is
            worse than one that admits what it could not read. */}
        {data.unreadable > 0 ? (
          <span className="ml-auto text-[11px] text-faint">
            {data.unreadable} board{data.unreadable === 1 ? "" : "s"} could not
            be read
          </span>
        ) : null}
      </div>

      <ul>
        {data.people.map((person) => (
          <li
            key={person.email ?? person.name}
            /* Banded, like the lists elsewhere in Pulse. */
            className="flex items-baseline gap-2 px-3 py-1 odd:bg-surface even:bg-app"
          >
            <span className="min-w-0 flex-1 truncate text-xs text-content">
              {person.name}
            </span>

            {person.open === 0 ? (
              <span className="shrink-0 text-[11px] text-sla-ok">
                Up to date
              </span>
            ) : (
              <>
                <span className="shrink-0 text-xs tabular-nums text-content">
                  {person.open}
                </span>
                {/* The denominator, so the count can be read as progress
                    rather than as an unanchored pile. */}
                <span className="shrink-0 text-[11px] tabular-nums text-faint">
                  of {person.total}
                </span>
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
